import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonObject
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

@Composable
internal fun TourVppEditor(token: String, id: String, closed: Boolean, onChanged: () -> Unit = {}) {
    var data by remember { mutableStateOf<JsonObject?>(null) }
    var qualification by remember { mutableStateOf<List<TourStageRow>>(emptyList()) }
    var names by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var playRevision by remember { mutableStateOf(0L) }
    var locked by remember { mutableStateOf(true) }
    var amount by remember { mutableStateOf("") }
    var numbers by remember { mutableStateOf("") }
    var dirty by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var retry by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()
    suspend fun reload() = coroutineScope {
        val vppTask = async { TourSeasonResultsApi.vpp(token, id) }
        val playTask = async { TourPlayApi.bundle(token, id) }
        val tournamentTask = async { TourOperationsApi.tournament(token, id) }
        val entriesTask = async { TourOperationsApi.entries(token, id) }
        val playersTask = async { TourApi.players(token) }
        val fresh = vppTask.await()
        val play = playTask.await()
        val tournament = tournamentTask.await()
        val players = playersTask.await().associate { it.id to (it.fullName?.takeIf { name -> name.isNotBlank() } ?: it.name) }
        names = entriesTask.await().associate { it.id to (players[it.playerId] ?: it.playerName) }
        qualification = play.qualification; playRevision = play.revision
        locked = closed || tournament.status != "running" || play.phase == "finished"
        val values = (if (locked) fresh.obj("published") else null) ?: fresh.obj("draft")
        amount = values?.text("amount")?.toDoubleOrNull()?.let { it.asDynamic().toFixed(0) as String }.orEmpty()
        numbers = values?.strings("numbers")?.joinToString(", ").orEmpty()
        data = fresh; dirty = false
    }
    LaunchedEffect(retry) {
        busy = true; error = null
        try { reload() } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { busy = false }
    }
    TourAdminStatus(data == null && busy, busy, error, "Načítám VPP…", "Obnovit VPP") { retry++ }
    val current = data ?: return
    val published = current.obj("published")
    val revision = current.text("revision").toLongOrNull() ?: 0L
    val parts = numbers.trim().split(Regex("[,;\\s]+")).filter { it.isNotEmpty() }
    val parsed = parts.mapNotNull { it.toIntOrNull() }.distinct()
    val numbersValid = parts.all { it.toIntOrNull()?.let { n -> n in 0..300 } == true }
    val amountValue = amount.trim().toLongOrNull()
    val amountValid = amount.isBlank() || (Regex("[0-9]+").matches(amount.trim()) && amountValue != null && amountValue in 0L..9999999999L)
    val playedScores = qualification.flatMap { listOfNotNull(it.game1, it.game2, it.game3) }.toSet()
    val unmatched = parsed.filter { it !in playedScores }
    val matches = if (numbersValid) qualification.mapNotNull { row ->
        val games = listOf(row.game1, row.game2, row.game3).mapIndexedNotNull { index, score ->
            score?.takeIf { it in parsed }?.let { index + 1 to it }
        }
        games.takeIf { it.isNotEmpty() }?.let { row to it }
    } else emptyList()
    val canPublish = amountValid && numbersValid && parsed.isNotEmpty() && unmatched.isEmpty() && (dirty || published == null || current.flag("stale"))
    Div(attrs = { classes("card") }) {
        H2 { Text("VPP") }
        P(attrs = { classes("muted") }) { Text("Zadejte částku v celých korunách a externě vylosovaný nához. Shody se hledají v čistých kvalifikačních hrách; každý hráč má jeden podíl.") }
        if (locked) P { Text(if (closed) "Ročník je uzavřený." else "Turnaj neprobíhá. VPP již nelze upravovat.") }
        else if (published != null) P(attrs = { attr("role", "status") }) {
            Text(if (dirty || current.flag("stale")) "Upravené hodnoty ještě nejsou zveřejněné." else "VPP je zveřejněno. Během turnaje můžete hodnoty upravit a znovu zveřejnit.")
        }
        Form(attrs = { classes("tour-form"); onSubmit {
            it.preventDefault()
            if (!busy && !locked && canPublish) scope.launch {
                busy = true; error = null
                try {
                    TourSeasonResultsApi.setVpp(token, id, revision, playRevision, if (amount.isBlank()) null else amountValue, parsed)
                    reload(); onChanged()
                } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
                finally { busy = false }
            }
        } }) {
            Label {
                Span { Text("Obsah VPP (Kč, nepovinné)") }
                Input(type = InputType.Text) {
                    attr("aria-label", "Obsah VPP (Kč, nepovinné)"); attr("inputmode", "numeric")
                    value(amount); if (busy || locked) disabled()
                    onInput { amount = it.value; dirty = true }
                }
            }
            TourField("Vylosovaná čísla (oddělená čárkou)", numbers, busy || locked) { numbers = it; dirty = true }
            if (!amountValid || !numbersValid) P(attrs = { classes("editor-error") }) { Text("Zadejte nezápornou částku v celých korunách a celá čísla 0–300.") }
            if (numbersValid && unmatched.isNotEmpty()) {
                P(attrs = { classes("editor-error", "tour-vpp-no-match"); attr("role", "alert") }) { Text("Nához ${unmatched.joinToString(", ")} v čistých kvalifikačních hrách není. Losujte znovu.") }
                Button(attrs = { type(ButtonType.Button); if (busy || locked) disabled(); onClick { numbers = ""; dirty = true } }) { Text("Zadat nový nához") }
            }
            if (matches.isNotEmpty()) Div(attrs = { classes("table-scroll"); attr("aria-live", "polite") }) {
                Table(attrs = { classes("tour-admin-table"); attr("aria-label", "Výherci VPP") }) {
                    Thead { Tr { Th { Text("Výherce") }; Th { Text("Shodné čisté hry v kvalifikaci") } } }
                    Tbody { matches.forEach { (row, games) -> Tr {
                        Td(attrs = { classes("tour-player-name") }) { Text(names[row.entryId] ?: row.playerName) }
                        Td { Text(games.joinToString { "${it.first}. hra: ${it.second}" }) }
                    } } }
                }
            }
            if (!locked) Button(attrs = { classes("primary"); type(ButtonType.Submit); if (busy || !canPublish) disabled() }) { Text("Zveřejnit") }
        }
    }
}
